import React, { useState } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { i18nIntl, isNotEmpty } from '../../lib/ep.js';

import ActionButton, { MainButton } from '../builder/ActionButton';
import ActionLink from '../builder/ActionLink';
import ButtonCollection from '../builder/ButtonCollection.js';
import Input from '../builder/Input';
import EPPlatformVersion from '../builder/EPPlatformVersion';
import FloatingLayer from '../builder/FloatingLayer';
import Model from '../builder/Model';
import SaveButton from '../builder/SaveButton';
import SectionSubtitle from '../builder/SectionSubtitle';
import SectionTitle from '../builder/SectionTitle';
import Select from '../builder/Select';
import Spinner from '../builder/Spinner';
import TextArea from '../builder/TextArea';

import Module from '../ui_items/Module.js';
import RibbonCollection from '../ui_items/RibbonCollection.js';

import { ChargeBeePortal, UpgradeSubscription } from './ChargeBee.js';
import { chargebee_item } from './Subscriptions.js';
import Tooltip from '../builder/Tooltip.js';

const Wrapper = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const is_test = props?.is_test ?? false;
	const next_billing_at = props?.next_billing_at ?? false;
	const is_sidebar = props?.is_sidebar ?? false;
	const pending = props?.pending ?? false;
	const is_chargebee = props?.is_chargebee ?? true;
	const automatic_renew = props?.automatic_renew ?? false;

	const ribbons00 = [{
		title:`${i18n("ID")}: ${props?.plan_id}`,
		autotranslate:false,
		not_ribbon:true,
		is_html:true,
	}];

	const getStatusTitle = () => {
		if(is_test) return "Trial";
		return "Active";
	};

	const getStatusColor = () => {
		if(is_test) return 'main';
		return 'green';
	};

	const status_ribbon = [];

	if(automatic_renew === false && !is_test) {
		status_ribbon.push({
			title:i18n("Non-renewing", 'uppercase'),
			color:'orange',
			is_light:false,
		});
	} else {
		status_ribbon.push({
			title:i18n(getStatusTitle(), 'uppercase'),
			color:getStatusColor(),
			is_light:false,
		});
	}

	const getRemainingDays = () => {
		const days_remaining = parseInt((!is_test) ? (props?.days_remain ?? '1') : (props?.days_remaining_trial ?? '1'));

		let usable_remaning;
		let usable_unit;
		let is_close = false;
		if(days_remaining > 365) {
			usable_remaning = Math.floor(days_remaining / 365);
			usable_unit = (usable_remaning > 1) ? "years" : "year";
		} else if(days_remaining > 30) {
			usable_remaning = Math.floor(days_remaining / 30);
			usable_unit = (usable_remaning > 1) ? "months" : "month";
		} else {
			usable_remaning = days_remaining;
			usable_unit = (usable_remaning > 1) ? "days" : "day";

			if(usable_remaning < 16) is_close = true;
		}

		return {
			usable_remaning,
			usable_unit,
			is_close,
		}
	};

	const data = [];

	let period_unit_sg = false;
	let period_unit_pl = false;
	switch(props.billing_period_unit) {
		case 'month':
		default:
			period_unit_sg = "month";
			period_unit_pl = "months";
			break;

		case 'year':
			period_unit_sg = "year";
			period_unit_pl = "years";
			break;
	}

	if(automatic_renew) {
		if(isNotEmpty(props, 'billing_period_unit')) {
			let msg;

			const billing_period = (isNotEmpty(props, 'billing_period')) ? parseInt(props?.billing_period ?? '1') : false;

			if(billing_period === 1 || billing_period === false) {
				msg = props.intl.formatMessage({ id:"Every {unit}" }, { unit:i18n(period_unit_sg) });
			} else {
				msg = props.intl.formatMessage({ id:"Every {num} {unit}" }, { num:props.billing_period, unit:i18n(period_unit_pl) });
			}
	
			data.push({
				k:'billing',
				dt:i18n("Billing"),
				dd:msg,
			});
		}

		const { usable_remaning, usable_unit } = getRemainingDays();

		data.push({
			k:'next_payment',
			dt:false,
			dd:props.intl.formatMessage({
				id:"Next renewal will be in {remaining} {unit}.",
			}, {
				remaining:usable_remaning,
				unit:i18n(usable_unit),
			}),
			tooltip:(props?.next_billing_at_agent ?? false) ? props.intl.formatMessage({
				id:"{date}{tz}",
			}, {
				date:`${EPDateFormat(props.next_billing_at_agent, 'L, HH:mm')}`,
				tz:(isNotEmpty(props, 'agent_tz')) ? `<br />(${props.agent_tz.replace(/\_/gi, " ")})` : null,
			}) : null,
		});

		if(isNotEmpty(props, 'remaining_billing_cycles') && parseInt(props.remaining_billing_cycles) > 0) {
			data.push({
				k:'next_cycles',
				dt:i18n("Pending periods"),
				dd:props.remaining_billing_cycles,
			});
		}
	} else if(is_test) {
		const { usable_remaning, usable_unit } = getRemainingDays();

		data.push({
			k:'trial_expire_date',
			dt:false,
			dd:props.intl.formatMessage({
				id:"You have {num} {unit} left in your trial.",
			}, {
				num:usable_remaning,
				unit:i18n(usable_unit),
			}),
			tooltip:(props?.trial_end_at_agent ?? false) ? props.intl.formatMessage({
				id:"{date}{tz}",
			}, {
				date:`${EPDateFormat(props.trial_end_at_agent, 'L, HH:mm')}`,
				tz:(isNotEmpty(props, 'agent_tz')) ? `<br />(${props.agent_tz.replace(/\_/gi, " ")})` : null,
			}) : null,
		});
	} else if(isNotEmpty(props, 'cancelled_at_utc') && isNotEmpty(props, 'cancelled_at_agent')) {
		data.push({
			k:'cancel_date',
			dt:false,
			dd:props.intl.formatMessage({
				id:"Ends on {agent_date}{tz}",
			}, {
				utc_date:`${EPDateFormat(props.cancelled_at_utc, 'L, HH:mm')}`,
				agent_date:`${EPDateFormat(props.cancelled_at_agent, 'L, HH:mm')}`,
				tz:(isNotEmpty(props, 'agent_tz')) ? `<br />(${props.agent_tz.replace(/\_/gi, " ")})` : null,
			}),
		});
	}

	const classes = ['single_plan', 'self_agent', 'gui_is_padded', `is_${(pending) ? 'pending' : 'active'}`];
	if(is_sidebar) classes.push('linkable');

	return (
		<div class={classes.join(" ")} onClick={(is_sidebar) ? () => { window.location.href = `${window.panel_url}plans`; } : null}>
			<div class="gui_pos_rel avatar_manage">
				<EPPlatformVersion
					version={props.version_type}
				/>
			</div>

			<div class="_info gui_flexbox_auto gui_flex_col gui_flex_space_between in_top" style={{ flexGrow:'1', gap:'0.8em' }}>
				<RibbonCollection
					items={ribbons00}
					_class={{ other:'gui_flex_center' }}
					style={{ '--main-text-color':'170, 170, 170' }}
				/>

				<RibbonCollection
					items={status_ribbon}
					_class={{ other:'gui_flex_col gui_flex_align_center' }}
				/>

				{(data.length > 0) && (
					<div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'0.3em', textAlign:'center' }}>
						{data.map((item) => {
							return (
								<div key={item.k}>
									{(item.dt !== false) && (
										<strong>{item.dt}: </strong>
									)}

									<span dangerouslySetInnerHTML={{ __html:item.dd }} />

									{(item?.tooltip ?? false) && (
										<Tooltip
											position='top'
										>{item.tooltip}</Tooltip>
									)}
								</div>
							)
						})}
					</div>
				)}

				{(props?.display_reactivate ?? false) && (
					<div class="not_in_top gui_text_align_center">
						<MainButton
							onClick={props.reactivatePlan}
						>Activate automatic renewal</MainButton>

						{(props.is_actioning) && (
							<Spinner
								force_bg={true}
								autotranslate={false}
							>{i18n("Reactivating Plan")} 😍</Spinner>
						)}
					</div>
				)}

				{(is_chargebee && !is_sidebar) && (
					<div class="gui_text_align_center">
						<ButtonCollection _class={{ other:'gui_flex_col' }}>
							<ChargeBeePortal
								type='link'
							>Manage plan</ChargeBeePortal>
						</ButtonCollection>

						{(!automatic_renew && !is_test) && (
							<div class='gui_not_in_top gui_balanced_text'>
								<p>{i18n("If you want to enable automatic renewal of the plan, click on Manage Plan.")}</p>
							</div>
						)}

						{(is_test && next_billing_at !== false) && (
							<div class='gui_not_in_top gui_balanced_text'>
								<p>{i18n("Billing for the plan will start after the trial period ends.")}</p>
							</div>
						)}
					</div>
				)}

				{props.children}
			</div>
		</div>
	)
};

const SubscriptionSingle = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const reason_model_default = { reason:false, reason_explain:"" };
	const magic_word_model_default = { magic_word:"" };

	const [ display_deactivation, setDisplayDeactivation ] = useState(false);
	const [ display_options, setDisplayOptions ] = useState(false);
	const [ is_actioning, setIsActioning ] = useState(false);
	const [ subscription, setSubscription ] = useState(props?.subscription ?? false);
	const [ magic_word_model, setMagicWordModel ] = useState(magic_word_model_default);
	const [ reason_model, setReasonModel ] = useState(reason_model_default);
	
	const expected_magic_word = props.intl.formatMessage({ id:"CANCEL" });

	const actual_plan = props?.actual_plan?.Plan ?? false;

	if(subscription === false) return null;

	const subscription_id = subscription.Subscription?.id ?? false;
	const is_annual = (subscription.Subscription.pay_mode == '1') ? true : false;
	const is_cancelled = (subscription.Subscription.status == '2') ? true : false;
	const is_test = (subscription.Subscription.test == '1') ? true : false;

	if((subscription?.Subscription ?? false) === false || subscription_id === false || actual_plan === false) return null;

	const changeReason = (k, v) => {
		setReasonModel({ ...reason_model, [k]:v });
	};

	const changeMagicWord = (k, v) => {
		setMagicWordModel({ ...magic_word_model, [k]:v });
	};

	const reactivatePlan = () => {
		setIsActioning(true);

		$.post(`/utils/actions_plan/reactivate/${subscription_id}`, { action:'reactivate', id:subscription_id }, (r) => {
			setTimeout(() => {
				if(r.status) {
					const _subscription = {
						...subscription,
						Subscription:{
							...subscription.Subscription,
							status:1,
							automatic_renew:1,
						},
					};
					setSubscription(_subscription);
					setIsActioning(false);

					if(props.on_change_plan != undefined) props.on_change_plan(_subscription);
					if(window.handleCommonHeader != undefined) window.handleCommonHeader.changeAutomaticRenew(true);
				}
			}, 1500);
		}, 'JSON');
	};

	const toggleDeactivation = () => {
		const _display_deactivation = !display_deactivation;
		setDisplayDeactivation(_display_deactivation);

		if(!_display_deactivation) {
			setMagicWordModel(magic_word_model_default);
			setReasonModel(reason_model_default);
		}
	};

	const deactivatePlan = (cb) => {
		setIsActioning(true);

		const data = {
			action:'cancel',
			id:subscription_id,
			reason:reason_model.reason,
		};
		if(reason_model.reason_explain != "") data.reason_explain = reason_model.reason_explain;

		$.post(`/utils/actions_plan/cancel/${subscription_id}`, data, (r) => {
			if(r.status) {
				const _subscription = {
					...subscription,
					Subscription:{
						...subscription.Subscription,
						status:2,
						automatic_renew:0,
					},
				};
				setSubscription(_subscription);
				setIsActioning(false);
				toggleDeactivation();

				if(props.on_change_plan != undefined) props.on_change_plan(_subscription);
				if(window.handleCommonHeader != undefined)window.handleCommonHeader.changeAutomaticRenew(false);
			}

			if(cb != undefined) cb();
		}, 'JSON');
	};

	const activateTrial = () => {
		$.post(`/utils/activate_test_subscription/${subscription_id}`, (r) => {
			window.location.href = `${window.panel_url}payments/pay/${r.p.Payment.id}`
		}, 'JSON');
	};

	const toggleOptions = () => {
		setDisplayOptions(!display_options);
	};

	const is_sidebar = props?.sidebar ?? false;
	const display_simplified_sidebar = false;

	const getVersionType = () => {
		switch(actual_plan.plan_type) {
			case '4':
				return 'premium';
	
			case '5':
				return 'wl';
	
			case '6':
				return 'corp';

			case '7':
				return 'corporate_plus'

			case '3':
				return 'basicpro';
	
			case '2':
			default:
				return 'basic';
		}
	};

	const getVersionTypeLink = () => {
		const currency = (window.currency == 'EUR') ? 'euros' : 'usd';
		const url_base = `${window.web_base_url}docs/`;

		switch(actual_plan.plan_type) {
			case '4':
				if(window.is_spanish) return `${url_base}suscripcion_premium_${currency}.pdf`;
				return `${url_base}premium_subscription_${currency}.pdf`;

			case '5':
				if(window.is_spanish) return `${url_base}suscripcion_marcablanca_${currency}.pdf`;
				return `${url_base}whitelabel_subscription_${currency}.pdf`;

			case '6':
			case '7':
				return false;

			case '3':
				if(window.is_spanish) return `${url_base}plan_basicpro_${currency}.pdf`;
				return `${url_base}basicpro_plan_${currency}.pdf`;

			case '2':
			default:
				if(window.is_spanish) return `${url_base}suscripcion_basica_${currency}.pdf`;
				return `${url_base}basic_subscription_${currency}.pdf`;
		}
	};

	let payment = subscription?.Payment ?? false;
	if(!payment && subscription.pending_payments != undefined) {
		const pending_payments_subscriptions = subscription.pending_payments.filter((obj) => {
			return (obj.Payment != undefined && obj.Payment.subscription_id == subscription_id) ? true : false;
		});

		if(pending_payments_subscriptions.length > 0) {
			payment = pending_payments_subscriptions[0]?.Payment ?? false;
		}
	}

	const next_payment_date = EPDateFormat(subscription.Subscription?.next_payment ?? payment.created, 'LL');
	const days_to_pay = subscription.Subscription.days_remain;
	let automatic_renew = (subscription.Subscription.automatic_renew == '1') ? true : false;
	if(automatic_renew == 1) automatic_renew = true;

	const subscription_status = ((subscription?.pending_payments ?? []).length > 0) ? 'pending' : 'active';
	const is_chargebee = (isNotEmpty(subscription.Subscription, 'external_id')) ? true : false;

	const wrapper_props = {
		...subscription.Subscription,
		automatic_renew,
		days_left:days_to_pay,
		display_options,
		is_sidebar:(is_sidebar || display_simplified_sidebar) ? true : false,
		intl:props.intl,
		next_payment_date,
		is_annual,
		is_chargebee,
		plan_id:(is_chargebee) ? subscription.Subscription.external_id : subscription_id,
		toggleOptions,
		version_type:getVersionType(),
		version_type_link:getVersionTypeLink(),
	};

	if(is_sidebar) {
		return (
			<Module _class={{ padding:false }}>
				<Wrapper
					{...wrapper_props}
					is_test={is_test}
				/>
			</Module>
		)
	}

	if(is_test) {
		return (
			<Module _class={{ padding:false }}>
				<Wrapper
					{...wrapper_props}
					is_test={true}
				>
					{/*(subscription_status != 'pending') && (
						<div class="not_in_top gui_text_align_center">
							{(!is_chargebee) && (
								<MainButton
									onClick={() => activateTrial()}
								>Activate a plan now</MainButton>
							)}

							{(is_chargebee) && (
								<UpgradeSubscription
									type='button'
									subscription_id={subscription_id}
									onSuccess={() => { window.location.href = `/payments/processing`; }}
									itemPriceId={chargebee_item(actual_plan.plan_type, 0, window.currency)}
								>Activate a plan now</UpgradeSubscription>
							)}

							<span style={{display:'inline-block', marginBlockStart:'0.4em'}}>{i18n("Before your trial expires")}</span>
						</div>
					)*/}
				</Wrapper>
			</Module>
		)
	}

	if(!is_cancelled && subscription_status == 'pending') {
		const price = actual_plan[`price_${(is_annual) ? 'annual_' : ''}${window.currency.toLowerCase()}`];
		const amount = ((window.currency == 'USD') ? '$' : '') + price + ' ' + ((window.currency == 'EUR') ? '€' : 'USD');

		return (
			<Module _class={{ padding:false }}>
				<Wrapper
					{...wrapper_props}
					pending={true}
				>
					<div class="automatic_renew_status">
						<div class="is_disabled">
							<p class='knk-alert gui_small_icon'>
								{i18n("Pending payment was on")} <strong>{next_payment_date}</strong>
							</p>
						</div>
					</div>

					<div class="not_in_top gui_text_align_center">
						<MainButton
							_href={`${window.panel_url}payments/pay/${payment.id}`}
						>Pay</MainButton>

						<span>{amount}</span>
					</div>
				</Wrapper>
			</Module>
		)
	}
	
	const display_upgrade_annual = (!is_annual && automatic_renew) ? true : false;
	//const display_upgrade_annual = (!is_annual && automatic_renew && (parseInt(subscription.Subscription.days_pay) >= 75)) ? true : false;
	const display_reactivate = ((!automatic_renew || is_cancelled) && !display_simplified_sidebar && !is_chargebee) ? true : false;

	const reasons = [
		{ k:'', v:`-- ${i18n("Select a reason")} --` },
		{ k:'no_actions', v:i18n("I'm not planning on launching promotional campaigns any time soon") },
		{ k:'not_satisfied_product', v:i18n("I'm not satisfied with the product") },
		{ k:'not_satisfied_service', v:i18n("I'm not satisfied with the service") },
		{ k:'alternative', v:i18n("I've found an alternative to Easypromos that better suits my needs") },
		{ k:'expensive', v:i18n("I can't afford the plan") },
		{ k:'other', v:i18n("Other reasons") },
	];

	const reason_selected = (reason_model.reason != "" && reasons.filter((r) => (r.k == reason_model.reason)).length > 0) ? true : false;
	const enable_cancel = (magic_word_model.magic_word == expected_magic_word && reason_selected) ? true : false;

	const upgradeable_plans = (props?.plans ?? []).filter((pl) => (pl.plan_type > actual_plan.plan_type));

	const display_message_renewed = (!is_chargebee && !is_cancelled && subscription_status != 'pending' && automatic_renew && !display_simplified_sidebar) ? true : false;
	const button_desactivate_renewal = (automatic_renew && !display_simplified_sidebar) ? true : false;

	return (
		<Module _class={{ padding:false }}>
			<Wrapper
				{...wrapper_props}
				reactivatePlan={reactivatePlan}
				display_reactivate={display_reactivate}
				is_actioning={is_actioning}
			>
				{(display_message_renewed) && (
					<div class="gui_text_align_center">
						{/*<p class={(!is_sidebar) ? 'knk-on gui_small_icon' : null}>{i18n("Automatic renewal is enabled")}</p>*/}

						{(button_desactivate_renewal && !is_sidebar && !is_chargebee) && (
							<ActionLink
								onClick={() => toggleDeactivation()}
							>{(automatic_renew) ? "Disable automatic renewal" : "Disable plan"}</ActionLink>
						)}
					</div>
				)}

				{(display_simplified_sidebar && !display_upgrade_annual) && (
					<div class="not_in_top gui_text_align_center">
						<ActionButton
							_href={`${window.panel_url}plans`}
						>Manage plan</ActionButton>
					</div>
				)}

				{(display_upgrade_annual && !is_chargebee) && (
					<div class="not_in_top gui_text_align_center">
						<MainButton
							_href={`${window.panel_url}plans/upgrade_to_annual_plan/${subscription_id}`}
						>Upgrade to annual plan</MainButton>

						<span style={{display:'inline-block', marginBlockStart:'0.4em'}}>{i18n("And save 2 months!")}</span>
					</div>
				)}

				{(display_upgrade_annual && is_chargebee) && (
					<div class="not_in_top gui_text_align_center">
						<UpgradeSubscription
							type='button'
							subscription_id={subscription_id}
							onSuccess={()=>{
								setCookie('processing_redirect','/plans',1,'.easypromosapp.com');	
								window.location.href=`/payments/processing`;
							}}
							autotranslate={false}
							itemPriceId={chargebee_item(actual_plan.plan_type, 1, window.currency)}
						>{props.intl.formatMessage({ id:"Upgrade to annual plan" })}</UpgradeSubscription>

						<span style={{display:'inline-block', marginBlockStart:'0.4em'}}>{i18n("And save 2 months!")}</span>
					</div>
				)}

				{(is_chargebee && automatic_renew && upgradeable_plans.length > 0) && (
					<div class='gui_not_in_top'>
						<p><strong>{i18n("Do you need more features?")}</strong></p>

						<ButtonCollection _class={{ other:'gui_flex_col gui_not_in_top_tiny' }}>
							{upgradeable_plans.map((pl) => {
								return (
									<UpgradeSubscription
										key={`type_${pl.type}`}
										type='link'
										subscription_id={subscription_id}
										onSuccess={() => {
											setCookie('processing_redirect', '/plans', 1, '.easypromosapp.com');	
											window.location.href = `/payments/processing`;
										}}
										autotranslate={false}
										itemPriceId={chargebee_item(pl.plan_type, subscription.Subscription.pay_mode, window.currency)}
									>{props.intl.formatMessage({ id:"Upgrade to {type} Plan" }, { type:pl.type_label })}</UpgradeSubscription>
								)
							})}
						</ButtonCollection>
					</div>
				)}
			</Wrapper>

			{(display_deactivation) && (
				<FloatingLayer backButton={() => toggleDeactivation()} _class={{ size:'small' }}>
					<div>
						<SectionTitle>Plan cancellation</SectionTitle>

						<SectionSubtitle>We're sorry to see you go. Could you give us some feedback? It will take you less than a minute and it helps us improve our products and plans.</SectionSubtitle>

						<Model model={reason_model} change={changeReason}>
							<Select
								name="reason"
								values={reasons}
							>Please, indicate the main reason for canceling your Easypromos subscription plan</Select>

							<TextArea
								name='reason_explain'
								_class={{ v_size:'small' }}
								autotranslate={false}
							>{`${i18n("Please use the following space to share your thoughts and opinion.")} (${i18n("Optional", 'lowercase')})`}</TextArea>
						</Model>

						<p class="gui_not_in_top_small gui_not_in_bottom">
							<strong>{props.intl.formatMessage({ id:"Even if you cancel, you can still use the plan without restrictions until {day}" }, { day:EPDateFormat(subscription.Subscription.next_payment, 'LL') })}.</strong>
						</p>

						<p><FormattedMessage id="To confirm the deletion, type {magic_word} (case sensitive) in the field and press the {label_cancel} button" values={{magic_word:`"${expected_magic_word}"`, label_cancel:`"${i18n("Cancel plan")}"` }} />:</p>

						<Model model={magic_word_model} change={changeMagicWord} classname='gui_not_in_bottom'>
							<Input
								name='magic_word'
								disabled={(!reason_selected) ? true : false}
								_class={{ size:50 }}
							>Confirmation word</Input>
						</Model>

						<ButtonCollection in_floating={true} _class={{ other:'gui_flex_col gui_flex_align_bottom' }}>
							<SaveButton
								disabled={!enable_cancel}
								onClick={(e, cb) => deactivatePlan(cb)}
							>Yes, cancel the subscription</SaveButton>

							<ActionLink
								onClick={() => toggleDeactivation()}
							>No, I don't want to cancel it yet</ActionLink>
						</ButtonCollection>
					</div>
				</FloatingLayer>
			)}
		</Module>
	)
};

export default injectIntl(SubscriptionSingle);